import {
  type Bbox,
  CircleSymbolizer,
  type Feature,
  GeomType,
  type JsonObject,
  type Label,
  type LabelSymbolizer,
  type Layout,
} from "protomaps-leaflet";
import type Point from "@mapbox/point-geometry";

export const getPointLabelMetrics = (
  renderScale = 1
): {
  fontSize: number;
  padding: number;
  yOffset: number;
  borderWidth: number;
  deduplicationDistance: number;
} => ({
  fontSize: 20 * renderScale,
  padding: 8 * renderScale,
  yOffset: -20 * renderScale,
  borderWidth: renderScale,
  deduplicationDistance: 10 * renderScale,
});

/**
 * pmtilesのPointのみを点として表示するクラス
 *
 * CircleSymbolizerは、ジオメトリタイプに関係なく点を描画しますが、
 * PointCircleSymbolizerはGeoJSONのPointデータ(geomType=1で表される)のみに描画を制限します。
 *
 * FeatureのジオメトリタイプがGeomType.Pointと一致する場合のみ親クラスの描画処理を実行し、
 * それ以外の場合は描画を行いません。
 */
class PointCircleSymbolizer extends CircleSymbolizer {
  draw(
    ctx: CanvasRenderingContext2D,
    geom: Point[][],
    z: number,
    feature: Feature
  ): void {
    // CircleSymbolizerはgeomTypeが何であっても点を描画してしまう
    // Pointのデータだけ点を打って欲しいのでgeomTypeで制限する
    // geomType=1がgeojsonのPointのデータ
    if (feature.geomType === GeomType.Point) {
      super.draw(ctx, geom, z, feature);
    }
  }
}

/**
 * pmtilesのプロパティに基づいて文字をレンダリングするためのクラス
 * textGetterに表示したい文字を生成する関数を渡して初期化してください
 */
class PointLabelSymbolizer implements LabelSymbolizer {
  // 文字を表示するための関数、pmtilsのpropertyの値が渡されるので、そこから表示したい値を取り出して文字列として返す
  private readonly _textGetter: (props: JsonObject) => string;
  // 文字に枠を表示するかしないかを決めるフラグ
  private readonly _drawRect: boolean;
  // 文字の枠内を塗りつぶす色を指定
  private readonly _color: string;
  // 文字の枠内を塗りつぶすかどうかを指定
  private readonly _fillRect: boolean;
  // 印刷解像度に合わせた描画倍率
  private readonly _renderScale: number;

  constructor(options: {
    textGetter: (props: JsonObject) => string;
    drawRect?: boolean;
    color?: string;
    fillRect?: boolean;
    renderScale?: number;
  }) {
    this._textGetter = options.textGetter;
    this._drawRect = options.drawRect ?? false;
    this._color = options.color ?? "transparent";
    this._fillRect = options.fillRect ?? false;
    this._renderScale = options.renderScale ?? 1;
  }

  place(
    layout: Layout,
    geom: Point[][],
    feature: Feature
  ): Label[] | undefined {
    if (feature.geomType === GeomType.Point) {
      const pt = geom[0][0];
      const name = this._textGetter(feature.props);
      if (!name) return undefined;

      const labelMetrics = getPointLabelMetrics(this._renderScale);
      const font = `${labelMetrics.fontSize}px sans-serif`;
      layout.scratch.font = font;

      const metrics = layout.scratch.measureText(String(name));
      const horizontalPadding = labelMetrics.padding;
      const verticalPadding = labelMetrics.padding;
      const width = metrics.width + horizontalPadding * 2;
      const ascent = metrics.actualBoundingBoxAscent + verticalPadding;
      const descent = metrics.actualBoundingBoxDescent + verticalPadding;
      const yOffset = labelMetrics.yOffset;

      const bbox: Bbox = {
        minX: pt.x - width / 2,
        minY: pt.y - ascent + yOffset,
        maxX: pt.x + width / 2,
        maxY: pt.y + descent + yOffset,
      };

      const draw = (ctx: CanvasRenderingContext2D): void => {
        ctx.font = font;
        ctx.globalAlpha = 0.9;
        ctx.textAlign = "center";

        // 文字枠を描画
        if (this._drawRect) {
          ctx.strokeStyle = "black";
          ctx.lineWidth = labelMetrics.borderWidth;
          ctx.strokeRect(
            -width / 2,
            -ascent + yOffset,
            width,
            ascent + descent
          );
        }
        // 文字枠内を塗りつぶす
        if (this._fillRect) {
          ctx.fillStyle = this._color;
          ctx.fillRect(-width / 2, -ascent + yOffset, width, ascent + descent);
        }
        // 文字本体の描画
        ctx.fillStyle = "black";
        ctx.fillText(String(name), 0, yOffset);
      };

      return [
        {
          anchor: pt,
          bboxes: [bbox],
          draw,
          deduplicationDistance: labelMetrics.deduplicationDistance,
        },
      ];
    }
    return undefined;
  }
}

export { PointCircleSymbolizer, PointLabelSymbolizer };
